import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'

import { listCities } from '../../api/reference'
import { SelectCombobox } from '../SelectCombobox'
import {
  hasVenueFormErrors,
  validateVenueForm,
  type VenueFormField,
  type VenueFormFieldErrors,
  type VenueFormValues,
} from '../../utils/venueFormValidation'

export type AddVenueModalProps = {
  open: boolean
  defaultCity?: string | null
  defaultHorecaType?: string | null
  submitting?: boolean
  formError?: string | null
  onDismiss: () => void
  onSubmit: (values: VenueFormValues) => Promise<void>
}

const emptyValues = (city: string, horecaType: string): VenueFormValues => ({
  inn: '',
  legalName: '',
  tradeName: '',
  city,
  horecaType,
})

const AddVenueField = ({
  error,
  label,
  children,
}: {
  error?: string
  label: ReactNode
  children: ReactNode
}) => (
  <label className={`echat-field${error ? ' echat-field--invalid' : ''}`}>
    <span className="echat-field__label">{label}</span>
    {children}
    {error ? (
      <span className="echat-field__error" role="alert">
        {error}
      </span>
    ) : null}
  </label>
)

export const AddVenueModal = ({
  open,
  defaultCity,
  defaultHorecaType,
  submitting = false,
  formError = null,
  onDismiss,
  onSubmit,
}: AddVenueModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [values, setValues] = useState<VenueFormValues>(() =>
    emptyValues(defaultCity ?? 'perm', defaultHorecaType ?? 'cafe'),
  )
  const [fieldErrors, setFieldErrors] = useState<VenueFormFieldErrors>({})

  useEffect(() => {
    if (!open) {
      return
    }
    setValues(emptyValues(defaultCity ?? 'perm', defaultHorecaType ?? 'cafe'))
    setFieldErrors({})
  }, [open, defaultCity, defaultHorecaType])

  useEffect(() => {
    void listCities().then(setCities)
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) {
        onDismiss()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    dialogRef.current?.focus()
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onDismiss, submitting])

  const clearFieldError = (field: VenueFormField) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current
      }
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const errors = validateVenueForm(values)
    setFieldErrors(errors)
    if (hasVenueFormErrors(errors)) {
      return
    }
    void onSubmit({
      inn: values.inn.trim(),
      legalName: values.legalName.trim(),
      tradeName: values.tradeName.trim(),
      city: values.city,
      horecaType: values.horecaType || defaultHorecaType || 'cafe',
    })
  }

  if (!open) {
    return null
  }

  return (
    <div
      className="market-add-venue-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) {
          onDismiss()
        }
      }}
    >
      <div
        ref={dialogRef}
        className="market-add-venue-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-venue-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="add-venue-title" className="market-add-venue-modal__title">
          Новое заведение
        </h2>
        <p className="market-add-venue-modal__subtitle">
          Заполните карточку предприятия — как при регистрации организации.
        </p>

        <form className="market-add-venue-form" onSubmit={handleSubmit} noValidate>
          <AddVenueField
            error={fieldErrors.inn}
            label={
              <>
                ИНН <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="echat-field__input"
              inputMode="numeric"
              value={values.inn}
              aria-invalid={fieldErrors.inn ? true : undefined}
              onChange={(event) => {
                setValues((current) => ({ ...current, inn: event.target.value }))
                clearFieldError('inn')
              }}
            />
          </AddVenueField>

          <AddVenueField
            error={fieldErrors.legalName}
            label={
              <>
                Юридическое название <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="echat-field__input"
              value={values.legalName}
              placeholder='ООО «Название»'
              aria-invalid={fieldErrors.legalName ? true : undefined}
              onChange={(event) => {
                setValues((current) => ({ ...current, legalName: event.target.value }))
                clearFieldError('legalName')
              }}
            />
          </AddVenueField>

          <AddVenueField
            error={fieldErrors.tradeName}
            label={
              <>
                Торговое название <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <input
              className="echat-field__input"
              value={values.tradeName}
              aria-invalid={fieldErrors.tradeName ? true : undefined}
              onChange={(event) => {
                setValues((current) => ({ ...current, tradeName: event.target.value }))
                clearFieldError('tradeName')
              }}
            />
          </AddVenueField>

          <AddVenueField
            error={fieldErrors.city}
            label={
              <>
                Город <span className="market-auth-field__required">*</span>
              </>
            }
          >
            <SelectCombobox
              embedded
              label="Город"
              value={values.city}
              options={cities.map((option) => ({ value: option.code, label: option.label }))}
              onChange={(city) => {
                setValues((current) => ({ ...current, city }))
                clearFieldError('city')
              }}
            />
          </AddVenueField>

          {formError ? (
            <p className="market-add-venue-modal__error" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="market-add-venue-modal__actions market-inline-actions">
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={submitting}
              onClick={onDismiss}
            >
              Отмена
            </button>
            <button type="submit" className="echat-btn echat-btn--primary" disabled={submitting}>
              {submitting ? 'Сохранение…' : 'Добавить заведение'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
